<template>
    <v-form ref="form" v-model="formValid" @submit.prevent="submit">
        <v-row class="px-2">
            <v-col cols="12" sm="6">
                <v-text-field v-model="formData.name" :label="$t('name')" :rules="requiredRules"
                    color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col v-if="isPartner" cols="12" sm="6">
                <v-text-field v-model="formData.link" :label="$t('link')" :rules="linkRules"
                    color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
                <v-img v-if="!isCreate && formData.imageUrl" :src="formData.imageUrl" max-width="160" max-height="80"
                    contain class="mb-2"></v-img>
                <v-file-input v-model="formData.image" :label="imageLabel" :rules="selectedImageRules"
                    accept="image/png,image/jpeg,image/gif,image/bmp,image/webp" prepend-icon="mdi-camera"
                    :hint="$t('partnerImageSizeHint')" persistent-hint color="teal lighten-2"></v-file-input>
            </v-col>
            <v-col cols="12" sm="3">
                <v-autocomplete v-model="formData.status" :label="$t('status')" :items="statuses"
                    item-text="name" item-value="value" :rules="requiredRules" hide-no-data
                    color="teal lighten-2" item-color="teal lighten-2"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
                <v-text-field v-model.number="formData.sortOrder" :label="$t('sortOrder')" type="number"
                    min="0" :rules="sortOrderRules" color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" class="d-flex justify-end">
                <v-btn color="teal" class="white--text" type="submit" :loading="isLoading" :disabled="!formValid">
                    {{ $t('save') }}
                </v-btn>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import { FilterService, PartnerManufacturerShowcasesService } from "../../services";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;

const DEFAULT_FORM_DATA = {
    name: '',
    link: '',
    image: null,
    imageUrl: null,
    status: 'active',
    sortOrder: 0,
};

export default {
    name: "PartnerManufacturerShowcaseForm",
    props: {
        mode: {
            type: String,
            default: 'create',
        },
        initialShowcase: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            formData: { ...DEFAULT_FORM_DATA },
            formValid: false,
            isLoading: false,
            statuses: [],
            requiredRules: [
                value => !!value || this.$t('requiredField'),
            ],
            imageRules: [
                value => !!value || this.$t('requiredField'),
                value => !value || value.size <= MAX_IMAGE_SIZE || this.$t('partnerImageMaxSize'),
            ],
            linkRules: [
                value => !!value || this.$t('requiredField'),
                value => /^https?:\/\/.+/.test(value) || this.$t('invalidUrl'),
            ],
            sortOrderRules: [
                value => value !== '' && value !== null && Number.isInteger(Number(value)) && Number(value) >= 0 || this.$t('invalidSortOrder'),
            ],
        }
    },
    computed: {
        isCreate() {
            return this.mode === 'create';
        },
        isPartner() {
            return this.isCreate || this.formData.sourceType === 'partner';
        },
        selectedImageRules() {
            if (this.isCreate) {
                return this.imageRules;
            }

            return [
                value => !value || value.size <= MAX_IMAGE_SIZE || this.$t('partnerImageMaxSize'),
            ];
        },
        imageLabel() {
            return this.isPartner ? this.$t('partnerImage') : this.$t('manufacturerImage');
        },
    },
    methods: {
        getStatuses() {
            FilterService.setAuth(this.$store.getters.token);

            return FilterService.getPartnerManufacturerShowcasesLayout()
                .then(res => {
                    const statusFilter = res.data.find(item => item.systemName === 'showcase_status');
                    this.statuses = statusFilter ? statusFilter.valuesData : [];
                })
                .catch(err => {
                    console.log(err);
                });
        },
        submit() {
            if (!this.$refs.form.validate()) {
                return;
            }

            this.isLoading = true;
            const data = new FormData();
            data.append('name', this.formData.name);

            if (this.isPartner) {
                data.append('link', this.formData.link);
            }

            if (this.formData.image) {
                data.append('image', this.formData.image);
            }

            data.append('status', this.formData.status);
            data.append('sortOrder', this.formData.sortOrder);

            const request = this.isCreate
                ? PartnerManufacturerShowcasesService.createPartner(data)
                : PartnerManufacturerShowcasesService.update(this.getUpdateData(data));

            request
                .then(res => {
                    this.$emit('submitted', res.data);
                    this.resetForm();
                })
                .catch(err => {
                    console.log(err);
                    this.$emit('failed', err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        getUpdateData(data) {
            data.append('id', this.formData.id);

            return data;
        },
        resetForm() {
            this.formData = { ...DEFAULT_FORM_DATA };
            this.$nextTick(() => {
                this.$refs.form.resetValidation();
            });
        },
    },
    created() {
        PartnerManufacturerShowcasesService.setAuth(this.$store.getters.token);
        this.getStatuses();
    },
    watch: {
        initialShowcase: {
            immediate: true,
            handler(value) {
                this.formData = value
                    ? { ...DEFAULT_FORM_DATA, ...value, image: null }
                    : { ...DEFAULT_FORM_DATA };

                this.$nextTick(() => {
                    if (this.$refs.form) {
                        this.$refs.form.resetValidation();
                    }
                });
            },
        },
    },
}
</script>
